import { useState } from 'react';
import { Button, Field, Modal, Pill, TextInput } from '@salonomy/ui';
import { useLoyaltyMember, useLoyaltyProgram, useRedeemPoints } from '../../data/loyalty.js';
import { ApiClientError } from '../../lib/api.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import './till.css';

/**
 * WS-13 — redeem loyalty POINTS as a discount at checkout, beside the voucher/discount modals. Points
 * are a non-monetary perk; redeeming converts points→pence on the SERVER (computeDiscount) and writes a
 * standard `discount` line (reasonCode 'loyalty'), which is what the till records.
 *
 * HONESTY (non-negotiable): every number shown is a SERVER number. The balance is the member's
 * DERIVED `balancePoints` (the signed sum of their ledger). The pence value of a redemption is NOT
 * computed here — we POST the points intent and record the server's returned `discount.discountPence`.
 * The "≈" pence preview shown next to a points amount is a soft, clearly-approximate hint off the
 * program's `redeemPencePerPoint` rate purely to orient staff; the receipt and the basket only ever show
 * the figure the till server returns. Redeem is idempotent on a per-intent key minted once on submit;
 * the button is disabled while pending so a double-tap can't redeem twice, and a 422 (program disabled,
 * balance too low, below the minimum) surfaces the API message faithfully.
 */
export function RedeemPointsModal({
  clientId,
  paymentId,
  appointmentId,
  onRedeemed,
  onClose,
}: {
  /** The basket's client. A walk-in (no client) can't have a loyalty balance. */
  clientId: string | null;
  /** The till payment the redemption reduces, if any (links the discount to the supply). */
  paymentId?: string | null;
  appointmentId?: string | null;
  /** Called with the server-computed reduction (pence) so the till records the discount line. */
  onRedeemed: (discountPence: number) => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const { data: program } = useLoyaltyProgram();
  const { data: member, isFetching, isError } = useLoyaltyMember(clientId);
  const redeem = useRedeemPoints();

  const minRedeem = program?.minRedeemPoints ?? 0;
  const pencePerPoint = program?.redeemPencePerPoint ?? 0;
  const balance = member?.balancePoints ?? 0;

  // The most they can spend is their whole balance; the floor is the program minimum (if they hold it).
  const [points, setPoints] = useState('');
  const parsed = Number(points);
  const pointsValid =
    Number.isInteger(parsed) &&
    parsed >= 1 &&
    parsed <= balance &&
    parsed >= (minRedeem > balance ? Number.POSITIVE_INFINITY : minRedeem);

  // A soft, clearly-approximate hint only — the server returns the real reduction.
  const approxPence = parsed > 0 ? parsed * pencePerPoint : 0;

  const noClient = clientId == null;
  const programOff = program != null && !program.enabled;
  const belowMin = balance < minRedeem || balance < 1;

  async function submit() {
    if (!clientId || !pointsValid) return;
    try {
      // Mint the idempotency key once per intent; the button is disabled while pending so a double-tap
      // can't redeem a second time, and the API is idempotent on this key.
      const result = await redeem.mutateAsync({
        clientId,
        points: parsed,
        paymentId: paymentId ?? undefined,
        appointmentId: appointmentId ?? undefined,
        idempotencyKey: crypto.randomUUID(),
      });
      // Record the SERVER-computed reduction — never a locally recomputed number.
      onRedeemed(result.discount.discountPence);
      toast.success(
        `Redeemed ${result.pointsRedeemed} points · ${money(result.discount.discountPence)} off · ${result.balancePoints} left`,
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not redeem points');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Redeem points"
      width={440}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {!noClient && !programOff && !belowMin ? (
            <Button onClick={submit} disabled={!pointsValid || redeem.isPending}>
              {redeem.isPending ? 'Redeeming…' : 'Redeem points'}
            </Button>
          ) : null}
        </>
      }
    >
      {noClient ? (
        <div className="muted" style={{ fontSize: 13.5 }}>
          This checkout has no client on it, so there&rsquo;s no loyalty balance to redeem. Add a
          client to the booking first.
        </div>
      ) : programOff ? (
        <div style={{ marginTop: 4 }}>
          <Pill tone="neutral">Loyalty off</Pill>
          <div className="muted" style={{ marginTop: 8, fontSize: 13.5 }}>
            This salon isn&rsquo;t running a loyalty program right now.
          </div>
        </div>
      ) : isError ? (
        <div className="muted" style={{ fontSize: 13.5 }}>
          Couldn&rsquo;t load this client&rsquo;s loyalty balance. Please try again.
        </div>
      ) : isFetching && !member ? (
        <div className="muted" style={{ fontSize: 13.5 }}>
          Loading balance…
        </div>
      ) : (
        <div className="till-voucher__found">
          <div className="till__line">
            <span>Points balance</span>
            <span className="mono">{balance}</span>
          </div>
          {minRedeem > 0 ? (
            <div className="till__line muted">
              <span>Minimum to redeem</span>
              <span className="mono">{minRedeem}</span>
            </div>
          ) : null}

          {belowMin ? (
            <div className="muted" style={{ marginTop: 8, fontSize: 13.5 }}>
              {balance < 1
                ? 'This client has no points to redeem yet.'
                : `This client needs at least ${minRedeem} points to redeem.`}
            </div>
          ) : (
            <>
              <Field
                label="Points to redeem"
                hint={
                  pencePerPoint > 0
                    ? `Up to ${balance}. Roughly ${money(approxPence)} off — the till shows the exact figure.`
                    : `Up to ${balance}.`
                }
                error={
                  parsed > balance
                    ? `This client only has ${balance} points.`
                    : parsed > 0 && parsed < minRedeem
                      ? `The minimum is ${minRedeem} points.`
                      : null
                }
              >
                <TextInput
                  autoFocus
                  inputMode="numeric"
                  value={points}
                  onChange={(e) => setPoints(e.target.value)}
                  placeholder={String(Math.max(minRedeem, 1))}
                />
              </Field>
              <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 4 }}>
                The exact discount is calculated by the till server and shown on the receipt. Points
                are a perk — redeeming them applies a discount, it doesn&rsquo;t move money in or
                out.
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
